import { BulkActions } from 'wflow-ui';
import {
  PeppolItem,
  PeppolItemDirection,
  PeppolItemStatus,
} from '../interfaces/peppol-item.interface';
import {
  PeppolAction,
  PeppolActionConfig,
} from '../interfaces/peppol-action.interface';

const PEPPOL_ACTION_CONFIG: Record<PeppolAction, PeppolActionConfig> = {
  [PeppolAction.CreateDocument]: {
    key: PeppolAction.CreateDocument,
    label: 'PEPPOL.ACTIONS.CreateDocument',
    icon: 'plus',
    showLabel: true,
    iconClass: 'xs',
    hidden: (item: PeppolItem) => !!item.documentId,
  },
  [PeppolAction.Archive]: {
    key: PeppolAction.Archive,
    label: 'PEPPOL.ACTIONS.Archive',
    icon: 'archive',
    showLabel: true,
    iconClass: 'xs',
    horizontalDivider: true,
  },
  [PeppolAction.ReturnToInbox]: {
    key: PeppolAction.ReturnToInbox,
    label: 'PEPPOL.ACTIONS.ReturnToInbox',
    icon: 'inbox_01',
    showLabel: true,
    iconClass: 'xs',
  },
  [PeppolAction.Accept]: {
    key: PeppolAction.Accept,
    label: 'PEPPOL.ACTIONS.Accept',
    icon: 'check_circle',
    showLabel: true,
    iconClass: 'xs',
    allowedStatuses: [
      PeppolItemStatus.AB,
      PeppolItemStatus.Delivered,
      PeppolItemStatus.IP,
      PeppolItemStatus.UQ,
      PeppolItemStatus.CA,
    ],
  },
  [PeppolAction.Reject]: {
    key: PeppolAction.Reject,
    label: 'PEPPOL.ACTIONS.Reject',
    icon: 'x_circle',
    showLabel: true,
    iconClass: 'xs',
    allowedStatuses: [PeppolItemStatus.AB, PeppolItemStatus.Delivered],
  },
  [PeppolAction.AcceptWithReservation]: {
    key: PeppolAction.AcceptWithReservation,
    label: 'PEPPOL.ACTIONS.AcceptWithReservation',
    icon: 'alert_triangle',
    showLabel: true,
    iconClass: 'xs',
    allowedStatuses: [
      PeppolItemStatus.AB,
      PeppolItemStatus.Delivered,
      PeppolItemStatus.IP,
      PeppolItemStatus.UQ,
    ],
  },
  [PeppolAction.InProcess]: {
    key: PeppolAction.InProcess,
    label: 'PEPPOL.ACTIONS.InProcess',
    icon: 'loading_01',
    showLabel: true,
    iconClass: 'xs',
  },
  [PeppolAction.UnderQuery]: {
    key: PeppolAction.UnderQuery,
    label: 'PEPPOL.ACTIONS.UnderQuery',
    icon: 'help_circle',
    showLabel: true,
    iconClass: 'xs',
    allowedStatuses: [
      PeppolItemStatus.AB,
      PeppolItemStatus.Delivered,
      PeppolItemStatus.IP,
    ],
  },
  [PeppolAction.Paid]: {
    key: PeppolAction.Paid,
    label: 'PEPPOL.ACTIONS.Paid',
    icon: 'currency_dollar_circle',
    showLabel: true,
    iconClass: 'xs',
    allowedStatuses: [
      PeppolItemStatus.AB,
      PeppolItemStatus.Delivered,
      PeppolItemStatus.IP,
      PeppolItemStatus.UQ,
      PeppolItemStatus.CA,
      PeppolItemStatus.AP,
    ],
  },
};

export const PEPPOL_STATUS_ACTIONS: readonly PeppolAction[] = [
  PeppolAction.Accept,
  PeppolAction.Reject,
  PeppolAction.AcceptWithReservation,
  PeppolAction.UnderQuery,
  PeppolAction.Paid,
];

export function peppolStatusActions(
  canReject: boolean,
): readonly PeppolAction[] {
  return canReject
    ? PEPPOL_STATUS_ACTIONS
    : PEPPOL_STATUS_ACTIONS.filter((action) => action !== PeppolAction.Reject);
}

export function acceptsStatusActions(
  message: PeppolItem | null,
): message is PeppolItem {
  return (
    !!message &&
    message.direction === PeppolItemDirection.Incoming &&
    !message.archivedAt
  );
}

export function visiblePeppolActions(
  actions: readonly PeppolAction[],
  item: PeppolItem,
): PeppolActionConfig[] {
  return actions
    .map((action) => PEPPOL_ACTION_CONFIG[action])
    .filter(
      (config) =>
        (config.allowedStatuses?.includes(item.status) ?? true) &&
        !config.hidden?.(item),
    )
    .map((config, index, list) =>
      config.horizontalDivider && index === list.length - 1
        ? { ...config, horizontalDivider: false }
        : config,
    );
}

export function buildPeppolActions(
  actions: readonly PeppolAction[],
  item: PeppolItem,
): BulkActions<PeppolAction> {
  return new BulkActions<PeppolAction>({
    contextualIcon: 'dots_vertical',
    contextualItems: visiblePeppolActions(actions, item),
  });
}
